import { queryKeys } from "@/lib/query-keys";
import vehicleService, { Vehicle } from "@/lib/services/vehicles";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

// ── List ──────────────────────────────────────────────────────────────────────

export function useVehicles(
    options?: Omit<UseQueryOptions<Vehicle[]>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: queryKeys.vehicles.list(),
        queryFn: () => vehicleService.getVehicles(),
        ...options,
    });
}

// ── Detail ────────────────────────────────────────────────────────────────────

export function useVehicle(
    id: number,
    options?: Omit<UseQueryOptions<Vehicle>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: queryKeys.vehicles.detail(id),
        queryFn: () => vehicleService.getVehicle(id),
        enabled: !!id,
        ...options,
    });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useDeleteVehicle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => vehicleService.deleteVehicle(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.vehicles.all });
        },
    });
}

export function useUpdateAvailability() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, is_available }: { id: number; is_available: boolean }) =>
            vehicleService.updateAvailability(id, is_available),
        onSuccess: (updated) => {
            qc.setQueryData<Vehicle>(
                queryKeys.vehicles.detail(updated.id),
                (existing) => {
                    if (!existing) {
                        return existing;
                    }

                    return { ...existing, is_available: updated.is_available };
                },
            );
            qc.invalidateQueries({ queryKey: queryKeys.vehicles.list() });
        },
    });
}

export function useUploadVehicleDocument() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, type, asset }: { id: number; type: string; asset: any }) =>
            vehicleService.uploadDocument(id, type, asset),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.vehicles.detail(id) });
        },
    });
}
